import React, { createContext, useContext, useMemo } from 'react';
import { CareType, CareTypeDisplayConfigsQuery, useCareTypeDisplayConfigsQuery } from '../graphQL';

export type CareTypeDisplayConfig = CareTypeDisplayConfigsQuery['careTypeDisplayConfigs'][number];

type CareTypeConfigContextValue = {
  careTypeDisplayConfigs: CareTypeDisplayConfig[];
  careTypeOptions: { id: CareType; label: string }[];
  /**
   * Graceful lookup: returns undefined for an unknown/absent careType. Use at
   * untyped boundaries, or as the primitive the getters below are built on.
   */
  findCareTypeDisplay: (input: string | null | undefined) => CareTypeDisplayConfig | undefined;
  /**
   * Strict lookup: throws if the careType is not configured. Use when you need
   * several fields off one config object and the careType is a typed value.
   */
  getCareTypeDisplay: (careType: string) => CareTypeDisplayConfig;
  /**
   * Per-field display getters. Each resolves the config and falls back
   * gracefully, so callers never restate a fallback. Accept `string | null |
   * undefined` so they work at untyped/nullable boundaries and can be passed
   * by reference (e.g. an activity-feed formatter's label resolver).
   *
   * Field choice: `getServiceTitle` for titles/headings/tabs/buttons/columns
   * and service-name references; `getServiceInlineTitle` for the care type used
   * as a common noun inside a sentence ("your X", "a X visit", "sponsored X").
   */
  getServiceTitle: (careType: string | null | undefined) => string;
  getServiceInlineTitle: (careType: string | null | undefined) => string;
};

export const CareTypeConfigContext = createContext<CareTypeConfigContextValue | null>(null);

/**
 * Build the value exposed by `CareTypeConfigContext`. Factored out so both the
 * real provider and the test render helper can share the same lookup logic.
 */
export const buildCareTypeConfigContextValue = (
  careTypeDisplayConfigs: CareTypeDisplayConfig[]
): CareTypeConfigContextValue => {
  const findCareTypeDisplay = (
    input: string | null | undefined
  ): CareTypeDisplayConfig | undefined => {
    if (typeof input !== 'string') return undefined;
    return careTypeDisplayConfigs.find(
      // FIXME: will be resolved in PDE-871
      // lowercasing is a hack to get around the mismatch between the care type
      // graphql enum (PascalCase) and the care type values in the database (lowercase)
      // when we update the database to match the graphql enum this can be removed and
      // this should be a strict match
      display => display.careType.toLowerCase() === input.toLowerCase()
    );
  };

  const getCareTypeDisplay = (careType: string): CareTypeDisplayConfig => {
    const config = findCareTypeDisplay(careType);
    if (!config) throw new Error(`No display config found for care type: ${careType}`);
    return config;
  };

  // The fallback policy for an unconfigured careType lives here, once.
  const getServiceTitle = (careType: string | null | undefined): string =>
    findCareTypeDisplay(careType)?.serviceTitle ?? careType ?? '';
  const getServiceInlineTitle = (careType: string | null | undefined): string =>
    findCareTypeDisplay(careType)?.serviceInlineTitle ?? careType?.toLowerCase() ?? '';

  return {
    careTypeDisplayConfigs,
    careTypeOptions: careTypeDisplayConfigs.map(config => ({
      id: config.careType,
      label: config.serviceTitle,
    })),
    findCareTypeDisplay,
    getCareTypeDisplay,
    getServiceTitle,
    getServiceInlineTitle,
  };
};

export const CareTypeConfigProvider: React.FC = ({ children }) => {
  const { data } = useCareTypeDisplayConfigsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const value = useMemo(
    () => (data ? buildCareTypeConfigContextValue(data.careTypeDisplayConfigs) : null),
    [data]
  );

  if (!value) return null;

  return <CareTypeConfigContext.Provider value={value}>{children}</CareTypeConfigContext.Provider>;
};

export const useCareTypeConfigContext = () => {
  const context = useContext(CareTypeConfigContext);
  if (!context) throw new Error('hook called outside of care type config context');
  return context;
};
